import { useEffect, useState } from 'react'

export interface UseImagePreloadResult {
  ready: boolean
}

/**
 * Preloads a single image URL and reports when the browser is ready to render
 * it without a network round-trip. Extracted for reuse across the Oracle
 * animation components (tarot card flip, oracle card landing) which all need
 * to gate a CSS transition on "the image is decoded and in cache".
 *
 * Behavior:
 * - When `enabled` is false or `url` is falsy, no Image is constructed and
 *   `ready` stays false. Callers use this to skip preloading until they
 *   actually need the asset.
 * - When both are set, constructs `new Image()`, assigns `src`, and flips
 *   `ready` to true on either `onload` or `onerror`.
 * - When the image is already cached, `img.complete` is true at construction
 *   and no `onload` will fire — we detect that synchronously and flip ready
 *   immediately.
 */
export const useImagePreload = (
  url: string | null | undefined,
  enabled: boolean,
): UseImagePreloadResult => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!enabled || !url) {
      // Reset so a previous true value doesn't leak across an enable/disable
      // toggle or a url -> null transition.
      setReady(false)
      return undefined
    }

    setReady(false)

    // Cleanup so a stale load (after url changes) doesn't flip ready while
    // the new url is still pending.
    let cancelled = false
    const img = new Image()
    const markReady = () => {
      if (!cancelled) setReady(true)
    }
    img.onload = markReady
    // Treat load errors as 'done' so a 404/CORS/DNS failure cannot stall the
    // animation indefinitely; downstream <img> tags have their own onError
    // fallbacks that show a placeholder.
    img.onerror = markReady
    img.src = url

    // If the browser already has the image cached, `complete` is true at
    // construction and `onload` will never fire — flip ready synchronously.
    if (img.complete) {
      markReady()
    }

    return () => { cancelled = true }
  }, [url, enabled])

  return { ready }
}
